import {mapGetters, mapActions} from 'vuex'
import {userPositionMarker} from 'js/lib/planner'
import TopologyRoutesService from 'js/services/api/topology/routes.js'
import TopologyStopsService from 'js/services/api/topology/stops.js'

import MapMixin from 'js/mixins/map.js'
import VueBarMixin from 'js/mixins/vue-bar.js'

import StopIndexContent from 'js/components/stops-index/content'
import StopShowContent from 'js/components/stop-show/content'
import StopShowHeader from 'js/components/stop-show/header'

import MarkersMixin from 'js/mixins/stops-markers-mixin'
import SidepanelMixin from 'js/mixins/stops-sidepanel-mixin'

import template from './index.pug'

// @vue/component
export default Vue.extend({
  name: 'stops-page',
  template: template(),
  mixins: [MapMixin, VueBarMixin, MarkersMixin, SidepanelMixin],
  components: {StopShowContent, StopIndexContent, StopShowHeader},

  data() {
    return {
      // Rutas cargadas en la vista
      routes: [],
      // Paraderos cargados en la vista en la pestaña de ver todos
      stops: [],
      stopSearchText: '',
      stopSearchTimeout: undefined,
      stopSearchRequestId: 0,
      searchedStops: false,
      stopSearchFailed: false,
      // Paraderos favoritos del usuario
      favoritesStops: [],
      // Paradero seleccionado
      selectedStop: {},
      requestTimeout: undefined,
      tabItems: [
        {text: this.$t('stops.sidepanel.tabs.favorites')},
        {text: this.$t('stops.sidepanel.tabs.all')}
      ],
      tab: 1
    }
  },

  computed: {
    ...mapGetters(['userPosition', 'operators', 'currentUser']),

    userIcon() {
      return userPositionMarker
    },

    // Paradas en el sidepanel
    filteredStops() {
      this.resetSidepanelScroll()

      // Si estamos en vista de favoritos, sólo se muestran los favoritos
      if (this.tab === 0) {
        return this.favoritesStops
      }

      return this.stops
    },

    // Paradas que aparecen en el mapa
    mapFilteredStops() {
      // Si hay un paradero, sólo se muestra ese
      if (this.isStopSelected) {
        return [this.selectedStop]
      }

      // Si estamos en vista de favoritos, sólo se muestran los favoritos
      if (this.tab === 0) {
        return this.favoritesStops
      // Si estamos en vista de mostrar todos, se muestran las paradas buscadas
      } else if (this.tab === 1) {
        return this.stops
      }

      return []
    },

    isStopSelected() {
      return !_.isEmpty(this.selectedStop)
    },



    // Codigo de paradero especificado en la URL
    urlStopCode() {
      return this.$route.query.code
    },

    // Hay codigo de paradero en la URL
    hasUrlStopCode() {
      return !!this.urlStopCode
    },

    hasSelectedStop() {
      return Object.keys(this.selectedStop).length > 0
    },

    userFavorites() {
      if (this.currentUser) {
        return this.$user.favorite_stop_ids
      }
      return []
    },

    user() {
      return !!this.currentUser
    }
  },

  watch: {
    $municipality: {
      handler(value) {
        if (value) {
          this.setMunicipalityConfig(value)
        }
      },
      immediate: true
    },
    sidePanelOpen() {
      setTimeout(() => this.map.invalidateSize(true), 250)
    },

    // Si inicio sesión, debo cargar los favoritos
    async $user(newUser, oldUser) {
      if (!newUser || oldUser) return

      await this.getFavoritesStops()
      this.selectFavoriteTab()
    }
  },

  async created() {
    if (this.hasUrlStopCode) await this.getSpecifiedStop()

    if (this.$user) await this.getFavoritesStops()

    if (!this.hasSelectedStop) await this.selectFavoriteTab()

    if (this.tab === 1 && !this.stops.length) this.getStops()
  },

  beforeDestroy() {
    clearTimeout(this.stopSearchTimeout)
  },

  methods: {
    ...mapActions([/* 'getOperators',  */'entryPointAction']),
    /*
     * Muestra la parada en el panel lateral al seleccionar una de la lista o al pulsar un marcador
     * del mapa
     */
    async showStop(stop, {centerMap = true} = {}) {
      this.selectedStop = stop

      const isFavorite = this.tab === 0 && this.isFavorite(stop)
      const entryPointObject = {
        previousLocation: 'map',
        routeId: '',
        sublineId: '',
        previousIsFavorites: isFavorite
      }

      if (!isFavorite) {
        stop.operator_id = await this.loadOperatorIdToStop(stop)
      }

      this.entryPointAction(entryPointObject)
      this.$store.commit('setStop', stop)

      if (centerMap) this.centerMap({lat: stop.lat, lng: stop.lng})

      this.sidePanelOpen = true
      this.customAccesibilityTabs.setup()
    },

    async loadOperatorIdToStop(stop) {
      const routeId = stop?.routes?.[0]?.id || stop?.routeIds?.[0] || stop?.route_ids?.[0]

      if (!routeId) return undefined

      try {
        const routeResponse = await TopologyRoutesService.getRoute(routeId)
        const currentOperator = this.operators.find(operator =>
          operator.id.toString() === routeResponse.data?.operator_ids?.[0]?.toString()
        )

        return currentOperator?.id
      } catch (error) {
        return undefined
      }
    },

    isFavorite(stop) {
      if (this.user && this.userFavorites.length > 0) {
        return _.includes(this.userFavorites, stop.id)
      }
      return false
    },

    /*
     * Oculta la parada seleccionada y vuelve a mostrar la lista
     */
    hideStop() {
      this.selectedStop = {}

      // Eliminar codigo de la parada de la URL
      if (this.hasUrlStopCode) this.deleteQueryParams()
    },

    deleteQueryParams() {
      this.$router.push({...this.$route, query: {}})
    },

    /*
     * Comprueba si un bus lleva más de 5 minutos sin reportar posición
     */
    checkInactiveBus(positionBus) {
      return moment(positionBus.last_valid_datetime).diff(moment().subtract(5, 'm'), 'm') < 0
    },

    /*
     * Obtiene la parada con el code que se especifica en la URL
     */
    async getSpecifiedStop() {
      const params = {
        municipality_id: this.$municipality.id,
        code: this.urlStopCode,
        template: 'complete'
      }

      const response = await TopologyStopsService.index(params)

      if (response.length) this.showStop(response[0])
    },

    searchStops(query) {
      clearTimeout(this.stopSearchTimeout)
      this.stopSearchText = query
      this.stopSearchFailed = false
      const requestId = ++this.stopSearchRequestId

      if (query.length < 2) {
        this.getStops()
        this.searchedStops = false
        return
      }

      this.stopSearchTimeout = setTimeout(async () => {
        try {
          const stops = await TopologyStopsService.selectable(this.$municipality.id, query)

          if (this.stopSearchRequestId === requestId) {
            this.stops = this.manageStops(this.filterStopsBySearch(stops, query))
            this.searchedStops = true
          }
        } catch (error) {
          if (this.stopSearchRequestId === requestId) {
            this.stops = []
            this.searchedStops = true
            this.stopSearchFailed = true
          }
        }
      }, 300)
    },

    selectFirstStop() {
      const stop = this.stops[0]

      if (stop) this.showStop(stop)
    },

    /*
     * Obtiene las paradas favoritas del usuario
     */
    async getStops() {
      const params = {
        municipality_id: this.$municipality.id,
        order_by: 'code',
        order_dir: 'asc',
        page: 1,
        per: 50,
        template: 'complete'
      }

      try {
        const stops = await TopologyStopsService.index(params)
        this.stops = this.manageStops(stops)
      } catch (error) {
        this.stops = []
      }
    },

    async getFavoritesStops() {
      const params = {
        municipality_id: this.$municipality.id,
        order_by: 'description',
        order_dir: 'asc',
        per: 0,
        template: 'complete'
      }

      const response = await TopologyStopsService.favorites({params, skipLoading: true})
      this.favoritesStops = this.manageStops(response)
    },

    normalizeStopSearchValue(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },

    hasStopDisplayCode(stop) {
      const displayCode = stop.display_code || stop.displayCode
      return displayCode && displayCode !== '...'
    },

    filterStopsBySearch(stops, query) {
      const normalizedQuery = this.normalizeStopSearchValue(query)

      return stops.filter(stop => {
        const displayCode = stop.display_code || stop.displayCode
        return [stop.name, displayCode].some(value =>
          this.normalizeStopSearchValue(value).includes(normalizedQuery)
        )
      })
    },

    manageStops(stops) {
      const stopsWithDisplayCode = stops.filter(stop => this.hasStopDisplayCode(stop))

      _.each(stopsWithDisplayCode, stop => {
        const displayCode = stop.display_code || stop.displayCode
        const routeIds = stop.route_ids || stop.routeIds || []

        stop.code_padded = displayCode.padStart(3, '0');
        const operatorUrls = this.operators
          .filter(operator => operator.route_ids.some(id => routeIds.includes(id)))
          .map(operator => operator.img_url.url || operator.logo_url || '');

        stop.logo_url = operatorUrls;
      });

      return _.sortBy(stopsWithDisplayCode, 'code_padded');
    },

    selectFavoriteTab() {
      return this.$nextTick(() => { if (this.favoritesStops.length) this.tab = 0 })
    },

    changeTab(index) {
      const nextTab = Number(index)

      if (nextTab !== this.tab) {
        this.tab = nextTab
      }

      if (nextTab === 1 && !this.stops.length) {
        this.getStops()
      }
    },

    setFavorite(stopId) {
      let updatedIds = this.$user.favorite_stop_ids

      if (_.includes(updatedIds, stopId)) {
        updatedIds = _.filter(updatedIds, item => item !== stopId)
      } else {
        updatedIds.push(stopId)
      }

      this.$auth.update({favorite_stop_ids: updatedIds}, true).then(() => {
        this.$user.favorite_stop_ids = updatedIds
        this.$store.commit('setCurrentUser', this.$user)
        this.getFavoritesStops()
      })
    },
    applyContrasteCss(){
      if(this.contraste){
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop ,.flex-middle,.ns-layout__footer__footer").css("cssText" ," color: #ffffff !important")
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("background-color" ,"#1e1e1e")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("cssText" ," color: #ffffff !important")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 ").css("background-color" ,"#1e1e1e")
        $("a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name").css("color" ,"#fff")
        $(".sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header").css( "cssText" ,"background-color: #1e1e1e !important")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("color" ,"#fff")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("background-color" ,"#1e1e1e")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ").css("color" ,"#fff")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.colorFondo ").css("background-color" ,"#1e1e1e")
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube").css("color" ,"#1e1e1e");
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube").css("background-color" ,"#fff")
        $(" .pb-d6 ").css("color" ,"#fff")  
        $("#iconorestablecer").css("color" ,"#1e1e1e");
        $("#iconorestablecer").css("background-color" ,"#fff")
        $("#textorestablecer").css("color" ,"#1e1e1e");
        $("#textorestablecer").css("background-color" ,"#fff")
        $(".text-gray2,.text-primary-hover ,.ns-layout__footer, .ns-layout__footer__footer,.hidden-cl ").css("cssText" ," color: #ffffff !important")
        $(".panel-schedules ,.px-d2 , .ns-session__nav--login ,.ns-layout__footer__footer").css( "cssText" ,"background-color: #1e1e1e !important")
        $(".ns-tabs ").css("background-color" ,"#1e1e1e")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
      
        $(".tablacss ").css( "cssText" ,"color: #1e1e1e !important")
        $(".tablacss ").css("background-color" ,"#fff")
        $(".ns-icon-angle-left,.sidepanel-map__card--item__stop ").css("color" ,"#fff")
        $(".ns-icon-angle-left ").css("background-color" ,"#1e1e1e")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
        $(".ns-layout__sidebar__map__menu").css("background-color" ,"#ffffff")
        $(".textomenu ").css( "cssText" ,"color: #1e1e1e !important")
        $("#linkhome ").css( "cssText" ,"color: #1e1e1e !important")
        $(".ns-layout__sidebar__map__menu").css("background-color" ,"#ffffff")
        $(".textomenu ").css( "cssText" ,"color: #1e1e1e !important")

        $(".submenuusuario").css("background-color" ,"#ffffff")
        $(".submenuusuario").css( "cssText" ,"color: #1e1e1e !important")

        $(".button-phone").css("border", "2px solid #ffffff")
        $(".option-menu").css("border", "none")
        $( ".active" ).css("background-color","transparent").css("border","2px solid #1e1e1e");
      }else{
        
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("color" ,"")
        $(".ns-layout ,.ns-layout__header, .text-black , .text-gray2 , .sidepanel-map__card--item__stop--route , .sidepanel-map__card--item__stop").css("background-color" ,"")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("color" ,"")
        $(".text-primary , .text-gray2-hover , .button-phone , .text-gray3 , .ns-layout__sidebar__map__menu").css("background-color" ,"")
        $("a ,.sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items , .pb-d6 , .sidepanel-map__card--item__point-name").css("color" ,"")
        $(".sidepanel-map__container, .recharge-home__recharges , .recharge-home__cities , .ns-layout__footer__items ,.pb-d6, .sidepanel-map__card--item__point-name,.flex-middle ,.sidepanel-map__header").css( "background-color" ,"")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("color" ,"")
        $(".sidepanel-map--planner__form , .uppercase-vz ,.hidden-xs ,.panel,.ns-field__label,.uppercase ,#fares-side-menu,.flex-column").css("background-color" ,"")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title ").css("color" ,"")
        $("label,.ns-tabs__item,.primay-dark,.text-small, .ns-icon-angle-right ,.h1-main-title,.text-main-title,.ns-session__nav--login ").css("background-color" ,"")
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube,.ns-tabs, .button-tabs,.text-primary-hover,.ns-layout__footer__footer,.hidden-cl").css("color" ,"");
        $(".ns-layout__header--city , .ns-button,.text-info , .ns-icon-youtube ,.mb-d1-2 ,.px-d2 ,.ns-tabs__item ,.panel-schedules ,.ns-tabs, .button-tabs,.ns-layout__footer__footer,.hidden-cl").css("background-color" ,"")
        $(" .pb-d6 ,.panel-schedules ,.px-d2 ").css("color" ,"")  
        $("#iconorestablecer").css("color" ,"#1e1e1e");
        $("#iconorestablecer").css("background-color" ,"#fff")
        $("#textorestablecer").css("color" ,"#1e1e1e");
        $("#textorestablecer").css("background-color" ,"#fff")
        $("#linkhome").css( "color" ,"")
        $(".textomenu").css( "color","")
        $(".tablacss ").css( "color","")
        $(".tablacss ").css("background-color" ,"")
        $(".ns-icon-angle-left ").css("color" ,"")
        $(".ns-icon-angle-left ,.colorFondo  ").css("background-color" ,"")
      }
    },
    setMunicipalityConfig(municipality) {
      this.pointerPosition = municipality.geo
      this.zoom = municipality.stops_map_zoom
      this.distance = municipality.stops_map_radio
    }
  }
 
})
